import { useState, useEffect } from 'react'
import { Mic, MessageSquare, X, Sparkles, Headphones } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useT } from '../../i18n/index.js'
import { VoiceRecorderFab } from './VoiceRecorderFab.js'
import { useMandatVoice } from '../../context/index.js'

export function AssistantFab() {
  const t = useT()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [voiceActive, setVoiceActive] = useState(false)

  const { mandatId, requestTTS, requestSTT } = useMandatVoice()

  // Escape ferme le menu — accessibilité clavier
  useEffect(() => {
    if (!expanded) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setExpanded(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [expanded])

  // Mode standalone : pas de mandat en contexte
  if (voiceActive) {
    return <VoiceRecorderFab autoStart onComplete={() => setVoiceActive(false)} />
  }

  function handleTTS() {
    setExpanded(false)
    requestTTS()
  }

  function handleSTT() {
    setExpanded(false)
    if (mandatId) {
      requestSTT()
    } else {
      setVoiceActive(true)
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Expanded menu */}
      {expanded && (
        <div role="menu" aria-label="Actions vocales" className="flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Écouter la fiche — uniquement si mandat en contexte */}
          {mandatId && (
            <button
              type="button"
              onClick={handleTTS}
              role="menuitem"
            className="flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-full bg-[var(--color-surface-raised)] border border-[var(--color-border)] text-[var(--color-text)] text-sm font-medium transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
              style={{ boxShadow: 'var(--shadow-elevated)' }}
            >
              <Headphones size={20} className="text-[var(--color-primary)]" />
              Écouter la fiche
            </button>
          )}

          {/* Dicter une note */}
          <button
            type="button"
            onClick={handleSTT}
            role="menuitem"
            className="flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-full bg-[var(--color-surface-raised)] border border-[var(--color-border)] text-[var(--color-text)] text-sm font-medium transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            style={{ boxShadow: 'var(--shadow-elevated)' }}
          >
            <Mic size={20} className="text-[var(--color-primary)]" />
            {mandatId ? 'Dicter une note' : (t.voice?.fab?.idle || 'Note vocale')}
          </button>

          {/* Chat Aria */}
          <button
            type="button"
            onClick={() => { setExpanded(false); navigate('/aria') }}
            role="menuitem"
            className="flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-full bg-[var(--color-surface-raised)] border border-[var(--color-border)] text-[var(--color-text)] text-sm font-medium transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            style={{ boxShadow: 'var(--shadow-elevated)' }}
          >
            <MessageSquare size={20} className="text-[var(--color-secondary)]" />
            {t.voice?.chat || 'Chat Aria'}
          </button>
        </div>
      )}

      {/* Main FAB */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="h-14 w-14 rounded-full flex items-center justify-center bg-[var(--color-primary)] text-[var(--color-text-inverse)] transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2"
        style={{ boxShadow: 'var(--shadow-elevated)' }}
        aria-label={expanded ? 'Fermer' : 'Assistant'}
        aria-expanded={expanded}
      >
        {expanded ? <X size={24} /> : <Sparkles size={24} />}
      </button>
    </div>
  )
}
